import { sanitizeAuthEventProperties } from '@/shared/analytics/sanitizeAuthEvent';
import { env } from '@/shared/config/env';
import posthog, { type PostHog } from 'posthog-js';

/**
 * PostHog product analytics client.
 *
 * Single initialized client, configured from the typed `env`. When
 * `POSTHOG_KEY` is absent (local dev / CI) the client is never initialized and
 * every helper here is a no-op — no network traffic, no console noise. That
 * "unconfigured" state is first-class and tested, rather than relying on the
 * library's behavior with a blank key.
 *
 * Autocapture is on (DOM interaction events); the library's automatic pageview
 * is off (`capture_pageview: false`) because this is a client-routed SPA — the
 * browser emits no navigation the library could hook, so `usePageviewTracking`
 * captures a `$pageview` per resolved TanStack Router location instead.
 */

/** Default ingest host: PostHog US cloud. Used when `POSTHOG_HOST` is unset. */
const DEFAULT_POSTHOG_HOST = 'https://us.i.posthog.com';

/** The initialized client, or `null` while unconfigured. */
let client: PostHog | null = null;

/**
 * Initialize the analytics client. Idempotent — safe to call more than once
 * (React StrictMode double-invokes), it only initializes on the first call with
 * a configured key. Returns early with no client when `POSTHOG_KEY` is empty.
 */
export function initAnalytics(): void {
  if (client) return;
  if (!env.POSTHOG_KEY) return;

  client = posthog.init(env.POSTHOG_KEY, {
    api_host: env.POSTHOG_HOST ?? DEFAULT_POSTHOG_HOST,
    autocapture: true,
    // SPA route changes are captured manually via usePageviewTracking.
    capture_pageview: false,
    before_send: (event) =>
      event ? { ...event, properties: sanitizeAuthEventProperties(event.properties) } : event,
  });

  // PostHog defaults to localStorage+cookie. Transition through its public
  // configuration API so existing state is migrated and the legacy cookie is
  // removed before the app starts making same-origin API requests.
  client?.set_config({ persistence: 'localStorage' });
}

/** The initialized client, or `null` when analytics is unconfigured. */
export function getAnalyticsClient(): PostHog | null {
  return client;
}

/** Capture a custom event. No-op while unconfigured. */
export function captureEvent(event: string, properties?: Record<string, unknown>): void {
  client?.capture(event, properties);
}

/**
 * Associate subsequent events with a stable distinct id and person properties.
 * No-op while unconfigured. Send only non-sensitive identity — never tokens or
 * asset content.
 */
export function identifyUser(distinctId: string, properties?: Record<string, unknown>): void {
  client?.identify(distinctId, properties);
}

/** Reset to a fresh anonymous identity (on sign-out). No-op while unconfigured. */
export function resetAnalytics(): void {
  client?.reset();
}
